import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ArrowPathIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import useDateRangeParams from "../../hooks/useDateRangeParams";
import { useIsMobile } from "../../helpers/useIsMobile";
import { ChevronIcon, CloseIcon, FilterIcon, SearchIcon } from "../reports/reportIcons";
import {
    DATE_PRESETS,
    INELIGIBLE_REASON_LABELS,
    OFFICIAL_CONTEST_WINDOW,
    PRIZE_COUNT,
    SUBMISSION_STATUS_LABELS,
    filterEntries,
    formatCount,
    formatZonedDate,
    formatMoney,
    formatPercent,
    formatRoas,
    formatSignedMoney,
    formatYmd,
    matchPreset,
    presetLabel,
    presetRange,
    reconciliationSearchText,
    sortRows,
    topEntries,
    varianceTone,
} from "./trybeContestFormat";

const toolbarButton = "inline-flex h-[34px] items-center gap-[7px] rounded-[7px] border border-[#ececef] bg-white px-[13px] text-[13px] font-medium text-[#0f1115] hover:bg-[#fafafb]";
const menuPanel = "absolute top-[calc(100%+4px)] z-30 min-w-[230px] rounded-lg border border-[#ececef] bg-white p-1 shadow-[0_8px_24px_rgba(15,17,21,0.08)]";
const menuItem = "flex h-[30px] w-full items-center justify-between gap-3 rounded-md px-2.5 text-left text-[12.5px] text-[#0f1115] hover:bg-[#f3f3f5]";
const dateInput = "h-[34px] rounded-[7px] border border-[#ececef] bg-white px-2.5 text-[13px] text-[#0f1115] outline-none focus:border-[#c9cbd3]";
const cellText = "text-[12.5px] text-[#4b5060]";
const numCell = "text-right text-[12.5px] tabular-nums text-[#4b5060]";
const headerBand = "h-9 items-center gap-3 border-b border-[#f3f3f5] bg-[#fafafb] px-4 text-[11.5px] font-semibold text-[#7b8090]";
const rowBase = "grid cursor-pointer gap-3 border-b border-[#f3f3f5] px-4 py-3 outline-none last:border-b-0 hover:bg-[#fafafb] focus-visible:bg-[#fafafb]";

const ENTRY_GRID_COMPACT = "grid-cols-[44px_minmax(0,1fr)_150px_110px_96px_64px]";
const ENTRY_GRID_WIDE = "grid-cols-[44px_minmax(0,1fr)_190px_150px_110px_64px_100px_64px]";
const WINNER_GRID_COMPACT = "grid-cols-[52px_minmax(0,1fr)_110px_96px_64px]";
const WINNER_GRID_WIDE = "grid-cols-[52px_minmax(0,1fr)_190px_110px_64px_100px_64px]";
const RECON_GRID_COMPACT = "grid-cols-[minmax(0,1fr)_120px_120px_120px_80px]";
const RECON_GRID_WIDE = "grid-cols-[minmax(0,1fr)_120px_120px_120px_80px_140px]";
const CREATOR_GRID_COMPACT = "grid-cols-[minmax(0,1fr)_110px_85px_85px_90px_110px]";
const CREATOR_GRID_WIDE = "grid-cols-[minmax(0,1fr)_120px_90px_90px_100px_120px_100px]";

const useMeasuredWidth = () => {
    const containerRef = useRef(null);
    const [width, setWidth] = useState(() => (
        typeof window === "undefined" ? 0 : window.innerWidth
    ));

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        const measure = () => {
            setWidth(container.getBoundingClientRect().width || window.innerWidth);
        };

        if (typeof ResizeObserver === "undefined") {
            measure();
            window.addEventListener("resize", measure);
            return () => window.removeEventListener("resize", measure);
        }

        const observer = new ResizeObserver(([entry]) => {
            setWidth(entry?.contentRect?.width || container.getBoundingClientRect().width || window.innerWidth);
        });
        observer.observe(container);
        measure();
        return () => observer.disconnect();
    }, []);

    return [containerRef, width];
};

const CHIP_TONES = {
    ok: "bg-[#ecfdf5] text-[#065f46]",
    warn: "bg-[#fff7ed] text-[#c2410c]",
    bad: "bg-[#fef2f2] text-[#b91c1c]",
    muted: "bg-[#f3f4f6] text-[#6b7280]",
};
const DOT_TONES = { ok: "bg-[#10b981]", warn: "bg-[#fb923c]", bad: "bg-[#ef4444]", muted: "bg-[#9ca3af]" };

const StatusChip = ({ tone, children, title }) => (
    <span title={title} className={`inline-flex h-[22px] max-w-full items-center gap-1.5 rounded px-2 text-[11.5px] font-medium ${CHIP_TONES[tone]}`}>
        <span className={`h-1.5 w-1.5 flex-none rounded-full ${DOT_TONES[tone]}`} />
        <span className="truncate">{children}</span>
    </span>
);

const NeutralChip = ({ children }) => (
    <span className="inline-flex h-[20px] max-w-full items-center rounded-md bg-[#f3f3f5] px-1.5 text-[11px] text-[#4b5060]">
        <span className="truncate">{children}</span>
    </span>
);

const EligibilityChip = ({ entry }) => {
    if (entry.eligible) return <StatusChip tone="ok">Eligible</StatusChip>;
    const labels = (entry.ineligibleReasons || []).map((reason) => INELIGIBLE_REASON_LABELS[reason] || reason);
    return <StatusChip tone="muted" title={labels.join("; ") || undefined}>{labels[0] || "Ineligible"}</StatusChip>;
};

const Thumb = ({ entry, size = "md" }) => {
    const [failed, setFailed] = useState(false);
    const large = size === "lg";
    const box = large ? "h-11 w-11 rounded-[9px]" : "h-9 w-9 rounded-[7px]";
    const showImage = entry.thumbnailUrl && !failed;
    return (
        <span className={`grid flex-none place-items-center overflow-hidden border border-[#ececef] bg-white ${box}`}>
            {showImage ? (
                <img alt="" src={entry.thumbnailUrl} onError={() => setFailed(true)} className="h-full w-full object-cover" />
            ) : (
                <img alt="" src="/brand/integrations/trybe.png" className={large ? "h-6 w-6 object-contain" : "h-[18px] w-[18px] object-contain"} />
            )}
        </span>
    );
};

const productLine = (entry) => {
    const products = (entry.products || []).join(", ");
    const pillars = (entry.pillars || []).join(", ");
    return { products: products || "—", pillars };
};

const ENTRY_SORT = {
    rank: (entry) => entry.rank,
    creator: (entry) => entry.creatorName || entry.trybeId,
    product: (entry) => productLine(entry).products,
    eligibility: (entry) => entry.eligible,
    gmv: (entry) => entry.gmv,
    orders: (entry) => entry.orders,
    metaSpend: (entry) => entry.metaSpend,
    roas: (entry) => entry.roas,
};

const CREATOR_SORT = {
    creator: (creator) => creator.creatorName,
    videosSubmitted: (creator) => creator.videosSubmitted,
    approved: (creator) => creator.approved,
    pending: (creator) => creator.pending,
    videosWithSales: (creator) => creator.videosWithSales,
    gmv: (creator) => creator.gmv,
    orders: (creator) => creator.orders,
};

const RECON_SORT = {
    creator: (row) => row.creatorName,
    shopifyGmv: (row) => row.shopifyGmv,
    trybeGmv: (row) => row.trybeGmv,
    variance: (row) => row.variance,
    variancePct: (row) => row.variancePct,
    orders: (row) => row.shopifyOrders,
};

const EntryNameCell = ({ entry }) => (
    <div className="flex min-w-0 items-center gap-3">
        <Thumb entry={entry} />
        <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-[13px] font-semibold text-[#0f1115]">{entry.creatorName || "Unknown creator"}</span>
                {entry.mediaType && entry.mediaType !== "video" && <NeutralChip>{entry.mediaType === "image" ? "Image" : entry.mediaType}</NeutralChip>}
                {entry.creatorBest && entry.eligible && entry.prizeRank === null && entry.rank !== null && <NeutralChip>Creator best</NeutralChip>}
            </div>
            <div className="mt-[2px] truncate text-[12px] text-[#7b8090]">
                <span className="font-mono text-[11.5px]">trybe={entry.trybeId}</span>
                {entry.adNames && entry.adNames[0] ? <span> · {entry.adNames[0]}</span> : null}
            </div>
        </div>
    </div>
);

const MobileMeta = ({ entry, isCompact }) => {
    const { products, pillars } = productLine(entry);
    return (
        <div className={`mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-[#7b8090] ${isCompact ? "hidden" : ""}`}>
            <span className="font-semibold tabular-nums text-[#0f1115]">{formatMoney(entry.gmv)}</span>
            <span>· {formatCount(entry.orders)} orders</span>
            <span>· Spend {formatMoney(entry.metaSpend, { cents: false })}</span>
            <span>· ROAS {formatRoas(entry.roas)}</span>
            <span className="w-full truncate">{products}{pillars ? ` · ${pillars}` : ""}</span>
        </div>
    );
};

const RankBadge = ({ value, prize }) => {
    if (value === null || value === undefined) return <span className="text-[12.5px] text-[#a2a6b3]">—</span>;
    if (prize) {
        return (
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#0f1115] text-[12px] font-semibold tabular-nums text-white">{value}</span>
        );
    }
    return <span className="text-[13px] font-semibold tabular-nums text-[#0f1115]">{value}</span>;
};

const Dropdown = ({ label, icon, options, value, onChange, menuAlign = "left", ariaLabel }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onClick = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
        const onKey = (event) => { if (event.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);
    return (
        <div className="relative" ref={ref}>
            <button type="button" aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} onClick={() => setOpen((o) => !o)} className={toolbarButton}>
                {icon}
                <span className="max-w-[260px] truncate">{label}</span>
                <ChevronIcon className="h-[14px] w-[14px] text-[#7b8090]" />
            </button>
            {open && (
                <div role="listbox" className={`${menuPanel} ${menuAlign === "right" ? "right-0" : "left-0"}`}>
                    {options.map((option) => (
                        <button
                            key={option.id}
                            type="button"
                            role="option"
                            aria-selected={value === option.id}
                            onClick={() => { onChange(option.id); setOpen(false); }}
                            className={`${menuItem} ${value === option.id ? "bg-[#f3f3f5] font-medium" : ""}`}
                        >
                            <span>{option.label}</span>
                            {option.hint !== undefined && <span className="text-[11px] text-[#a2a6b3]">{option.hint}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const Segmented = ({ options, value, onChange }) => (
    <div className="inline-flex h-[34px] items-center rounded-[7px] border border-[#ececef] bg-white p-0.5">
        {options.map((option) => (
            <button
                key={option.id}
                type="button"
                aria-pressed={value === option.id}
                onClick={() => onChange(option.id)}
                className={`inline-flex h-[28px] items-center gap-1.5 rounded-[5px] px-2.5 text-[12.5px] font-medium ${value === option.id ? "bg-[#e6e6ea] text-[#0f1115]" : "text-[#7b8090] hover:text-[#0f1115]"}`}
            >
                {option.label}
                {option.count !== undefined && <span className="text-[11px] tabular-nums text-[#a2a6b3]">{option.count}</span>}
            </button>
        ))}
    </div>
);

const Stat = ({ label, value, hint }) => (
    <div className="min-w-0 rounded-[10px] border border-[#ececef] bg-white px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-[.08em] text-[#7b8090]">{label}</div>
        <div className="mt-1 truncate text-[18px] font-[650] tabular-nums tracking-[-.01em] text-[#0f1115]">{value}</div>
        {hint && <div className="mt-0.5 truncate text-[11.5px] text-[#a2a6b3]">{hint}</div>}
    </div>
);

const DetailRow = ({ label, children }) => (
    <>
        <dt className="text-[12px] text-[#7b8090]">{label}</dt>
        <dd className="min-w-0 break-words text-[12.5px] text-[#0f1115]">{children}</dd>
    </>
);

const EntryDrawer = ({ entry, onClose }) => {
    useEffect(() => {
        const onKey = (event) => { if (event.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);
    const reasons = entry.ineligibleReasons || [];
    return (
        <aside
            aria-label={`${entry.creatorName || entry.trybeId} details`}
            className="fixed inset-0 z-[110] w-full overflow-auto bg-white md:sticky md:top-0 md:z-auto md:h-screen md:w-[400px] md:flex-none md:border-l md:border-[#ececef] md:shadow-[-8px_0_24px_rgba(15,17,21,0.04)]"
        >
            <div className="px-5 pb-8 pt-[18px]">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-start gap-3">
                        <Thumb entry={entry} size="lg" />
                        <div className="min-w-0">
                            <h2 className="text-[16px] font-[650] tracking-[-.015em] text-[#0f1115]">{entry.creatorName || "Unknown creator"}</h2>
                            <p className="mt-[3px] truncate font-mono text-[11.5px] text-[#7b8090]">trybe={entry.trybeId}</p>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close details" className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-[7px] text-[#7b8090] hover:bg-[#f3f3f5]">
                        <CloseIcon className="h-[13px] w-[13px]" />
                    </button>
                </div>

                <div className={`mt-4 rounded-[8px] px-3 py-2.5 text-[12.5px] ${entry.eligible ? "bg-[#ecfdf5] text-[#065f46]" : "bg-[#f3f4f6] text-[#4b5060]"}`}>
                    {entry.eligible
                        ? `Eligible. Rank ${entry.rank} of eligible entries${entry.prizeRank ? `, prize position ${entry.prizeRank}` : ""}.`
                        : `Not eligible: ${reasons.map((reason) => INELIGIBLE_REASON_LABELS[reason] || reason).join("; ") || "unknown reason"}.`}
                </div>

                <h3 className="mt-5 text-[11px] font-semibold uppercase tracking-[.08em] text-[#7b8090]">Performance in range</h3>
                <dl className="mt-2 grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-2">
                    <DetailRow label="Shopify GMV">{formatMoney(entry.gmv)}</DetailRow>
                    <DetailRow label="Net sales">{formatMoney(entry.netSales)}</DetailRow>
                    <DetailRow label="Orders">{formatCount(entry.orders)}</DetailRow>
                    <DetailRow label="Meta spend">{formatMoney(entry.metaSpend)}</DetailRow>
                    <DetailRow label="ROAS">{formatRoas(entry.roas)}<span className="text-[#a2a6b3]"> · Shopify GMV ÷ Meta spend</span></DetailRow>
                    <DetailRow label="Meta purchases">{formatMoney(entry.metaPurchaseValue)}<span className="text-[#a2a6b3]"> · Meta-reported, not used to rank</span></DetailRow>
                    <DetailRow label="Overall rank">{entry.overallRank ?? "—"}</DetailRow>
                </dl>

                <h3 className="mt-5 text-[11px] font-semibold uppercase tracking-[.08em] text-[#7b8090]">Trybe submission</h3>
                <dl className="mt-2 grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-2">
                    <DetailRow label="Status">{entry.status ? (SUBMISSION_STATUS_LABELS[entry.status] || entry.status) : "—"}</DetailRow>
                    <DetailRow label="Media">{entry.mediaType ? (entry.mediaType === "video" ? "Video" : entry.mediaType === "image" ? "Image" : entry.mediaType) : "—"}</DetailRow>
                    <DetailRow label="Program">{entry.programName || "—"}</DetailRow>
                    <DetailRow label="Products">{(entry.products || []).join(", ") || "—"}</DetailRow>
                    <DetailRow label="Pillar">{(entry.pillars || []).join(", ") || "—"}</DetailRow>
                    <DetailRow label="Angles">{(entry.angles || []).join("; ") || "—"}</DetailRow>
                    <DetailRow label="Submitted">{formatZonedDate(entry.submittedAt)}</DetailRow>
                    <DetailRow label="Version">{entry.version ?? "—"}</DetailRow>
                    <DetailRow label="Ads (all time)">
                        {entry.adsCount === null || entry.adsCount === undefined
                            ? "—"
                            : `${formatCount(entry.adsCount)} ads${entry.adsFirstDay ? ` · ${formatYmd(entry.adsFirstDay)}–${formatYmd(entry.adsLastDay)}` : ""}`}
                    </DetailRow>
                    <DetailRow label="Submission ID"><span className="font-mono text-[11.5px]">{entry.submissionId || "—"}</span></DetailRow>
                </dl>

                <h3 className="mt-5 text-[11px] font-semibold uppercase tracking-[.08em] text-[#7b8090]">Ad names on Shopify orders</h3>
                {(entry.adNames || []).length ? (
                    <ul className="mt-2 space-y-1.5">
                        {entry.adNames.map((name) => (
                            <li key={name} className="break-all rounded-md bg-[#f7f7f8] px-2.5 py-1.5 font-mono text-[11.5px] text-[#4b5060]">{name}</li>
                        ))}
                    </ul>
                ) : (
                    <p className="mt-2 text-[12.5px] text-[#7b8090]">No ad names recorded for this range.</p>
                )}
            </div>
        </aside>
    );
};

const EmptyState = ({ title, hint, action }) => (
    <div className="px-6 py-14 text-center">
        <p className="text-[13.5px] font-semibold text-[#0f1115]">{title}</p>
        {hint && <p className="mx-auto mt-1 max-w-[520px] text-[12.5px] text-[#7b8090]">{hint}</p>}
        {action}
    </div>
);

const SortHeader = ({ label, sortKey, sort, onClick, align = "left", className = "" }) => {
    const active = sort?.key === sortKey;
    return (
        <div
            role="columnheader"
            aria-sort={active ? (sort.direction === "desc" ? "descending" : "ascending") : "none"}
            className={className}
        >
            <button
                type="button"
                onClick={onClick}
                className={`flex h-full w-full items-center gap-1 font-semibold ${align === "right" ? "justify-end text-right" : "text-left"} ${active ? "text-[#0f1115]" : "text-[#7b8090]"}`}
            >
                <span>{label}</span>
                {active && <span aria-hidden="true">{sort.direction === "desc" ? "↓" : "↑"}</span>}
            </button>
        </div>
    );
};

const TrybeContest = () => {
    const isMobile = useIsMobile();
    const [contentRef, contentWidth] = useMeasuredWidth();
    const isCompact = contentWidth >= 640;
    const isWide = contentWidth >= 1000;
    const { startDate, endDate, setDateRange } = useDateRangeParams(OFFICIAL_CONTEST_WINDOW.startDate, OFFICIAL_CONTEST_WINDOW.endDate);
    const [draft, setDraft] = useState({ startDate, endDate });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [view, setView] = useState("winners");
    const [sortByView, setSortByView] = useState({});
    const [onePrizePerCreator, setOnePrizePerCreator] = useState(true);
    const [eligibility, setEligibility] = useState("all");
    const [query, setQuery] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const searchRef = useRef(null);
    const toggleSort = (targetView, key, defaultDirection) => {
        setSortByView((current) => {
            const activeSort = current[targetView];
            return {
                ...current,
                [targetView]: {
                    key,
                    direction: activeSort?.key === key
                        ? (activeSort.direction === "desc" ? "asc" : "desc")
                        : defaultDirection,
                },
            };
        });
    };
    const entryGridClass = (winnersView) => {
        if (isWide) return winnersView ? WINNER_GRID_WIDE : ENTRY_GRID_WIDE;
        if (isCompact) return winnersView ? WINNER_GRID_COMPACT : ENTRY_GRID_COMPACT;
        return "grid-cols-[32px_minmax(0,1fr)]";
    };
    const creatorGridClass = isWide
        ? CREATOR_GRID_WIDE
        : isCompact
            ? CREATOR_GRID_COMPACT
            : "grid-cols-1";
    const reconGridClass = isWide
        ? RECON_GRID_WIDE
        : isCompact
            ? RECON_GRID_COMPACT
            : "grid-cols-1";

    useEffect(() => { setDraft({ startDate, endDate }); }, [startDate, endDate]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        axios.get("/api/v1/trybe-contest/leaderboard", { params: { startDate, endDate } })
            .then(({ data: response }) => {
                if (cancelled) return;
                setData(response?.data ?? response);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.response?.data?.error || err?.message || "Could not load the leaderboard");
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [startDate, endDate, reloadKey]);

    useEffect(() => {
        const onKey = (event) => {
            const target = event.target;
            const isEditableTarget = target instanceof HTMLElement && (
                target.tagName === "INPUT" ||
                target.tagName === "TEXTAREA" ||
                target.tagName === "SELECT" ||
                target.isContentEditable
            );
            if (event.key === "/" && !isEditableTarget) {
                event.preventDefault();
                searchRef.current?.focus();
            }
        };
        document.addEventListener("keydown", onKey, true);
        return () => document.removeEventListener("keydown", onKey, true);
    }, []);

    const presetId = matchPreset({ startDate, endDate });
    const range = data?.window || { startDate, endDate };
    const entries = useMemo(() => data?.entries || [], [data]);
    const creators = useMemo(() => data?.creators || [], [data]);
    const reconciliation = useMemo(() => data?.reconciliation || [], [data]);
    const filteredEntries = useMemo(() => filterEntries(entries, { query, eligibility }), [entries, query, eligibility]);
    const sortedEntries = useMemo(
        () => sortRows(filteredEntries, sortByView.entries, ENTRY_SORT),
        [filteredEntries, sortByView.entries],
    );
    const filteredCreators = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? creators.filter((creator) => String(creator.creatorName || "").toLowerCase().includes(q)) : creators;
    }, [creators, query]);
    const sortedCreators = useMemo(
        () => sortRows(filteredCreators, sortByView.creators, CREATOR_SORT),
        [filteredCreators, sortByView.creators],
    );
    const winners = useMemo(() => {
        const top = topEntries(data, { onePrizePerCreator });
        const q = query.trim().toLowerCase();
        return q ? filterEntries(top, { query: q }) : top;
    }, [data, onePrizePerCreator, query]);
    const filteredRecon = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? reconciliation.filter((row) => reconciliationSearchText(row).includes(q)) : reconciliation;
    }, [reconciliation, query]);
    const sortedRecon = useMemo(
        () => sortRows(filteredRecon, sortByView.reconciliation, RECON_SORT),
        [filteredRecon, sortByView.reconciliation],
    );
    const selected = useMemo(() => entries.find((entry) => entry.trybeId === selectedId) || null, [entries, selectedId]);
    const closeDrawer = useCallback(() => setSelectedId(null), []);

    const eligibleCount = entries.filter((entry) => entry.eligible).length;
    const summary = data?.summary || {};
    const submissionStats = data?.submissionStats || {};
    const freshness = data?.freshness || {};

    const applyPreset = (id) => {
        if (id === "custom") return;
        setDateRange(presetRange(id));
    };
    const applyDraft = (next) => {
        setDraft(next);
        if (next.startDate && next.endDate && next.startDate <= next.endDate) setDateRange(next);
    };

    const presetOptions = [
        ...DATE_PRESETS.map((preset) => ({
            id: preset.id,
            label: preset.id === "official" ? `Official window (${formatYmd(OFFICIAL_CONTEST_WINDOW.startDate)}–${formatYmd(OFFICIAL_CONTEST_WINDOW.endDate)})` : preset.label,
        })),
        ...(presetId === "custom" ? [{ id: "custom", label: "Custom range" }] : []),
    ];
    const viewOptions = [
        { id: "winners", label: `Top ${PRIZE_COUNT}` },
        { id: "entries", label: "Videos with sales", count: entries.length },
        { id: "creators", label: "Creators", count: creators.length },
        { id: "reconciliation", label: isMobile ? "Recon" : "Reconciliation", count: reconciliation.length },
    ];
    const eligibilityOptions = [
        { id: "all", label: "All entries", hint: entries.length },
        { id: "eligible", label: "Eligible", hint: eligibleCount },
        { id: "ineligible", label: "Not eligible", hint: entries.length - eligibleCount },
    ];
    const eligibilityLabel = eligibilityOptions.find((option) => option.id === eligibility)?.label;

    const freshnessLine = [
        freshness.shopifyLatestDate ? `Shopify sales through ${formatYmd(freshness.shopifyLatestDate)}` : "No Shopify sales loaded yet",
        freshness.trybeCreatorLatestDate ? `Trybe GMV through ${formatYmd(freshness.trybeCreatorLatestDate)}` : null,
        freshness.metaLatestDate ? `Meta spend through ${formatYmd(freshness.metaLatestDate)}` : null,
    ].filter(Boolean).join(" · ");

    const notStarted = Boolean(data?.window?.notStarted);
    const notStartedState = (
        <EmptyState
            title={`This range starts ${formatYmd(range.startDate)}`}
            hint={`Nothing has been sold in it yet. Rankings fill in the morning after its first day, once the 2:00 AM Shopify sync runs.`}
        />
    );

    const labelRow = (label, shown, total) => (
        <div className="mb-2 mt-4 flex items-center justify-between gap-3">
            <div className="text-[11px] font-semibold uppercase tracking-[.08em] text-[#7b8090]">{label}</div>
            <div className="flex items-center gap-2.5">
                <div className="whitespace-nowrap text-[11.5px] tabular-nums text-[#a2a6b3]">{shown} of {total}</div>
            </div>
        </div>
    );

    const renderEntryRow = (entry, { winnersView = false } = {}) => {
        const { products, pillars } = productLine(entry);
        const isSelected = entry.trybeId === selectedId;
        const rankValue = winnersView ? (onePrizePerCreator ? entry.prizeRank : entry.rank) : entry.rank;
        return (
            <div
                key={entry.trybeId}
                role="button"
                tabIndex={0}
                aria-label={`${entry.creatorName || entry.trybeId}, ${formatMoney(entry.gmv)}`}
                onClick={() => setSelectedId(isSelected ? null : entry.trybeId)}
                onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelectedId(entry.trybeId); } }}
                className={`${rowBase} ${entryGridClass(winnersView)} ${isCompact ? "min-h-[56px] items-center" : ""} ${isSelected ? "bg-[#f3f3f5]" : ""}`}
            >
                <div className={isCompact ? "" : "pt-1.5"}><RankBadge value={rankValue} prize={winnersView} /></div>
                <div className="min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <EntryNameCell entry={entry} />
                        {!winnersView && <div className={isCompact ? "hidden" : "flex-none"}><EligibilityChip entry={entry} /></div>}
                    </div>
                    <MobileMeta entry={entry} isCompact={isCompact} />
                </div>
                <div className={`${isWide ? "block" : "hidden"} min-w-0`}>
                    <div className={`${cellText} truncate`}>{products}</div>
                    {pillars && <div className="mt-[2px] truncate text-[11.5px] text-[#a2a6b3]">{pillars}</div>}
                </div>
                {!winnersView && <div className={`${isCompact ? "block" : "hidden"} min-w-0`}><EligibilityChip entry={entry} /></div>}
                <div className={`${isCompact ? "block" : "hidden"} ${numCell} font-semibold text-[#0f1115]`}>{formatMoney(entry.gmv)}</div>
                <div className={`${isWide ? "block" : "hidden"} ${numCell}`}>{formatCount(entry.orders)}</div>
                <div className={`${isCompact ? "block" : "hidden"} ${numCell}`}>{formatMoney(entry.metaSpend, { cents: false })}</div>
                <div className={`${isCompact ? "block" : "hidden"} ${numCell}`}>{formatRoas(entry.roas)}</div>
            </div>
        );
    };

    const entryHeader = (winnersView) => {
        if (winnersView) {
            return (
                <div className={`${headerBand} ${isCompact ? "grid" : "hidden"} ${entryGridClass(true)}`}>
                    <div>Prize</div>
                    <div>Creator · submission</div>
                    <div className={isWide ? "block" : "hidden"}>Product · pillar</div>
                    <div className="text-right">Shopify GMV</div>
                    <div className={`${isWide ? "block" : "hidden"} text-right`}>Orders</div>
                    <div className="text-right">Meta spend</div>
                    <div className="text-right">ROAS</div>
                </div>
            );
        }
        const sort = sortByView.entries;
        return (
            <div className={`${headerBand} ${isCompact ? "grid" : "hidden"} ${entryGridClass(false)}`}>
                <SortHeader label="Rank" sortKey="rank" sort={sort} onClick={() => toggleSort("entries", "rank", "asc")} />
                <SortHeader label="Creator · submission" sortKey="creator" sort={sort} onClick={() => toggleSort("entries", "creator", "asc")} />
                <SortHeader label="Product · pillar" sortKey="product" sort={sort} onClick={() => toggleSort("entries", "product", "asc")} className={isWide ? "block" : "hidden"} />
                <SortHeader label="Eligibility" sortKey="eligibility" sort={sort} onClick={() => toggleSort("entries", "eligibility", "desc")} />
                <SortHeader label="Shopify GMV" sortKey="gmv" sort={sort} onClick={() => toggleSort("entries", "gmv", "desc")} align="right" />
                <SortHeader label="Orders" sortKey="orders" sort={sort} onClick={() => toggleSort("entries", "orders", "desc")} align="right" className={isWide ? "block text-right" : "hidden"} />
                <SortHeader label="Meta spend" sortKey="metaSpend" sort={sort} onClick={() => toggleSort("entries", "metaSpend", "desc")} align="right" />
                <SortHeader label="ROAS" sortKey="roas" sort={sort} onClick={() => toggleSort("entries", "roas", "desc")} align="right" />
            </div>
        );
    };

    const renderWinners = () => (
        <>
            {labelRow(onePrizePerCreator ? "Prize positions · one per creator" : `Top ${PRIZE_COUNT} eligible entries`, winners.length, PRIZE_COUNT)}
            <div className="overflow-hidden rounded-[10px] border border-[#ececef] bg-white">
                {entryHeader(true)}
                {notStarted ? notStartedState : winners.length ? winners.map((entry) => renderEntryRow(entry, { winnersView: true })) : (
                    <EmptyState
                        title={entries.length ? "No eligible entries in this range" : "No Trybe-attributed Shopify sales in this range"}
                        hint={entries.length
                            ? "Only approved video submissions with net sales in the range can place. Check All entries for the reasons."
                            : `Sales appear here the day after a Shopify order arrives with trybe=<id> in its UTM content. ${freshnessLine}.`}
                    />
                )}
            </div>
            <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-[12.5px] text-[#4b5060]">
                <input type="checkbox" checked={onePrizePerCreator} onChange={(event) => setOnePrizePerCreator(event.target.checked)} className="h-3.5 w-3.5 rounded border-[#c9cbd3] accent-[#0f1115]" />
                One prize max per creator (a creator's lower entries are skipped and the next creator moves up)
            </label>
        </>
    );

    const renderEntries = () => (
        <>
            {labelRow(eligibility === "all" ? "All entries" : eligibilityLabel, sortedEntries.length, entries.length)}
            <div className="overflow-hidden rounded-[10px] border border-[#ececef] bg-white">
                {entryHeader(false)}
                {notStarted ? notStartedState : sortedEntries.length ? sortedEntries.map((entry) => renderEntryRow(entry)) : (
                    <EmptyState
                        title={entries.length ? "No entries match" : "No Trybe-attributed Shopify sales in this range"}
                        hint={entries.length ? "Try a different search or eligibility filter." : freshnessLine}
                        action={entries.length ? (
                            <button type="button" onClick={() => { setQuery(""); setEligibility("all"); }} className={`${toolbarButton} mt-4`}>Reset filters</button>
                        ) : null}
                    />
                )}
            </div>
        </>
    );

    const renderReconciliation = () => (
        <>
            {labelRow("Creator reconciliation · Shopify vs Trybe", filteredRecon.length, reconciliation.length)}
            <p className="mb-2 text-[12px] text-[#7b8090]">
                Informational only: rankings use Shopify sales per trybe_id and are never rescaled to Trybe's creator totals. Shopify days are America/Denver, Trybe days are UTC.
            </p>
            <div className="overflow-hidden rounded-[10px] border border-[#ececef] bg-white">
                <div className={`${headerBand} ${isCompact ? "grid" : "hidden"} ${reconGridClass}`}>
                    <SortHeader label="Creator" sortKey="creator" sort={sortByView.reconciliation} onClick={() => toggleSort("reconciliation", "creator", "asc")} />
                    <SortHeader label="Shopify GMV" sortKey="shopifyGmv" sort={sortByView.reconciliation} onClick={() => toggleSort("reconciliation", "shopifyGmv", "desc")} align="right" />
                    <SortHeader label="Trybe GMV" sortKey="trybeGmv" sort={sortByView.reconciliation} onClick={() => toggleSort("reconciliation", "trybeGmv", "desc")} align="right" />
                    <SortHeader label="Variance" sortKey="variance" sort={sortByView.reconciliation} onClick={() => toggleSort("reconciliation", "variance", "desc")} align="right" />
                    <SortHeader label="%" sortKey="variancePct" sort={sortByView.reconciliation} onClick={() => toggleSort("reconciliation", "variancePct", "desc")} align="right" />
                    <SortHeader label="Orders · conversions" sortKey="orders" sort={sortByView.reconciliation} onClick={() => toggleSort("reconciliation", "orders", "desc")} align="right" className={isWide ? "block text-right" : "hidden"} />
                </div>
                {notStarted ? notStartedState : sortedRecon.length ? sortedRecon.map((row) => {
                    const tone = varianceTone(row.variancePct, row.variance);
                    return (
                        <div key={row.creatorId || row.creatorName} className={`grid gap-3 border-b border-[#f3f3f5] px-4 py-3 last:border-b-0 ${isCompact ? "min-h-[52px] items-center" : ""} ${reconGridClass}`}>
                            <div className="min-w-0">
                                <div className="flex min-w-0 items-center gap-2">
                                    <span className="truncate text-[13px] font-semibold text-[#0f1115]">{row.creatorName || "Unknown creator"}</span>
                                    {row.trybeCoverageComplete === false && <NeutralChip>Trybe days missing</NeutralChip>}
                                </div>
                                <div className="mt-[2px] truncate font-mono text-[11.5px] text-[#7b8090]">
                                    {(row.trybeIds || []).length ? row.trybeIds.map((id) => `trybe=${id}`).join(" · ") : "No trybe-tagged Shopify orders"}
                                </div>
                                <div className={`${isCompact ? "hidden" : "flex"} mt-1.5 flex-wrap gap-x-2 text-[12px] tabular-nums text-[#7b8090]`}>
                                    <span>Shopify {formatMoney(row.shopifyGmv)}</span>
                                    <span>· Trybe {formatMoney(row.trybeGmv)}</span>
                                    <span className={tone === "ok" ? "" : tone === "warn" ? "text-[#c2410c]" : "text-[#b91c1c]"}>· {formatSignedMoney(row.variance)} ({formatPercent(row.variancePct)})</span>
                                </div>
                            </div>
                            <div className={`${isCompact ? "block" : "hidden"} ${numCell}`}>{formatMoney(row.shopifyGmv)}</div>
                            <div className={`${isCompact ? "block" : "hidden"} ${numCell}`}>{formatMoney(row.trybeGmv)}</div>
                            <div className={`${isCompact ? "flex" : "hidden"} justify-end`}>
                                <StatusChip tone={tone}>{formatSignedMoney(row.variance)}</StatusChip>
                            </div>
                            <div className={`${isCompact ? "block" : "hidden"} ${numCell}`}>{formatPercent(row.variancePct)}</div>
                            <div className={`${isWide ? "block" : "hidden"} ${numCell}`}>{formatCount(row.shopifyOrders)} · {formatCount(row.trybeConversions)}</div>
                        </div>
                    );
                }) : (
                    <EmptyState title={reconciliation.length ? "No creators match" : "Nothing to reconcile in this range"} hint={reconciliation.length ? "Try a different search." : freshnessLine} />
                )}
            </div>
        </>
    );

    const renderCreators = () => (
        <>
            {labelRow("Creators", sortedCreators.length, creators.length)}
            <div className="overflow-hidden rounded-[10px] border border-[#ececef] bg-white">
                <div className={`${headerBand} ${isCompact ? "grid" : "hidden"} ${creatorGridClass}`}>
                    <SortHeader label="Creator" sortKey="creator" sort={sortByView.creators} onClick={() => toggleSort("creators", "creator", "asc")} />
                    <SortHeader label="Videos submitted" sortKey="videosSubmitted" sort={sortByView.creators} onClick={() => toggleSort("creators", "videosSubmitted", "desc")} align="right" />
                    <SortHeader label="Approved" sortKey="approved" sort={sortByView.creators} onClick={() => toggleSort("creators", "approved", "desc")} align="right" />
                    <SortHeader label="Pending" sortKey="pending" sort={sortByView.creators} onClick={() => toggleSort("creators", "pending", "desc")} align="right" />
                    <SortHeader label="With sales" sortKey="videosWithSales" sort={sortByView.creators} onClick={() => toggleSort("creators", "videosWithSales", "desc")} align="right" />
                    <SortHeader label="GMV" sortKey="gmv" sort={sortByView.creators} onClick={() => toggleSort("creators", "gmv", "desc")} align="right" />
                    <SortHeader label="Orders" sortKey="orders" sort={sortByView.creators} onClick={() => toggleSort("creators", "orders", "desc")} align="right" className={isWide ? "block text-right" : "hidden"} />
                </div>
                {notStarted ? notStartedState : sortedCreators.length ? sortedCreators.map((creator, index) => (
                    <div key={creator.creatorId || creator.trybeIds?.[0] || creator.creatorName || index} className={`grid gap-3 border-b border-[#f3f3f5] px-4 py-3 last:border-b-0 ${isCompact ? "min-h-[52px] items-center" : ""} ${creatorGridClass}`}>
                        <div className="min-w-0">
                            <div className="truncate text-[13px] font-semibold text-[#0f1115]">{creator.creatorName || "Unknown creator"}</div>
                            <div className="mt-[2px] truncate text-[11.5px] text-[#7b8090]">{formatCount(creator.videosWithSales)} with sales</div>
                            {!isCompact && (
                                <div className="mt-1.5 flex flex-wrap gap-x-2 text-[12px] tabular-nums text-[#7b8090]">
                                    <span>{formatCount(creator.videosSubmitted)} submitted</span>
                                    <span>· {formatCount(creator.approved)} approved</span>
                                    <span>· {formatCount(creator.pending)} pending</span>
                                    <span>· {formatMoney(creator.gmv)}</span>
                                </div>
                            )}
                        </div>
                        <div className={`${isCompact ? "block" : "hidden"} ${numCell}`}>{formatCount(creator.videosSubmitted)}</div>
                        <div className={`${isCompact ? "block" : "hidden"} ${numCell}`}>{formatCount(creator.approved)}</div>
                        <div className={`${isCompact ? "block" : "hidden"} ${numCell}`}>{formatCount(creator.pending)}</div>
                        <div className={`${isCompact ? "block" : "hidden"} ${numCell}`}>{formatCount(creator.videosWithSales)}</div>
                        <div className={`${isCompact ? "block" : "hidden"} ${numCell}`}>{formatMoney(creator.gmv)}</div>
                        <div className={`${isWide ? "block" : "hidden"} ${numCell}`}>{formatCount(creator.orders)}</div>
                    </div>
                )) : (
                    <EmptyState
                        title={creators.length ? "No creators match" : "No submissions in this range"}
                        hint={creators.length ? "Try a different search." : freshnessLine}
                    />
                )}
            </div>
        </>
    );

    return (
        <div className="min-h-screen bg-[#f7f7f8] text-[#0f1115] antialiased">
            <div className="flex min-h-screen">
                <div className="flex min-h-screen min-w-0 flex-1 flex-col">
                    <header className="px-4 pt-[14px] sm:px-7">
                        {!isMobile && <div className="text-[10.5px] font-semibold uppercase tracking-[.14em] text-[#7b8090]">Eadin Internal Tool Suite</div>}
                        <div className="mt-1.5 flex items-center justify-between gap-3 md:items-end">
                            <h1 className="shrink-0 whitespace-nowrap text-[20px] font-[650] tracking-[-.02em]">{isMobile ? "Trybe Contest" : "Trybe Creator Contest"}</h1>
                            {isMobile ? (
                                <div className="flex items-center gap-2">
                                    <Dropdown label={presetId === "official" ? "Official window" : presetLabel(presetId, { startDate, endDate }).replace(/\/\d{4}/g, "")} options={presetOptions} value={presetId} onChange={applyPreset} menuAlign="right" ariaLabel="Date range" />
                                    <button type="button" aria-label="Refresh" onClick={() => setReloadKey((k) => k + 1)} className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-[7px] border border-[#ececef] bg-white text-[#7b8090] hover:bg-[#fafafb]">
                                        <ArrowPathIcon className="h-[15px] w-[15px]" />
                                    </button>
                                </div>
                            ) : (
                                <div className="text-right text-[12px] text-[#7b8090]">
                                    Ranked by Shopify sales on orders tagged trybe=&lt;id&gt;
                                    <div className="text-[11.5px] text-[#a2a6b3]">{freshnessLine}</div>
                                </div>
                            )}
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2.5">
                            <label className="flex h-9 min-w-[200px] flex-1 items-center gap-2.5 rounded-[10px] border border-[#ececef] bg-white px-3.5 shadow-[0_1px_2px_rgba(15,17,21,0.04)] focus-within:border-[#c9cbd3]">
                                <SearchIcon className="h-[15px] w-[15px] flex-none text-[#7b8090]" />
                                <input
                                    ref={searchRef}
                                    type="search"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    onKeyDown={(event) => { if (event.key === "Escape") setQuery(""); }}
                                    placeholder="Search creators, trybe IDs, products, pillars, or ad names…"
                                    className="min-w-0 flex-1 border-0 bg-transparent text-[13.5px] text-[#0f1115] outline-none placeholder:text-[#a2a6b3]"
                                />
                                <kbd className="hidden rounded border border-[#ececef] bg-[#f7f7f8] px-1.5 py-px font-sans text-[10.5px] text-[#a2a6b3] sm:block">/</kbd>
                            </label>
                            {!isMobile && (
                                <>
                                    <Dropdown label={presetLabel(presetId, { startDate, endDate })} options={presetOptions} value={presetId} onChange={applyPreset} ariaLabel="Date range" icon={<FilterIcon className="h-[14px] w-[14px] text-[#7b8090]" />} />
                                    <div className="flex items-center gap-1.5">
                                        <input type="date" aria-label="Start date" value={draft.startDate} max={draft.endDate} onChange={(event) => applyDraft({ ...draft, startDate: event.target.value })} className={dateInput} />
                                        <span className="text-[12px] text-[#a2a6b3]">to</span>
                                        <input type="date" aria-label="End date" value={draft.endDate} min={draft.startDate} onChange={(event) => applyDraft({ ...draft, endDate: event.target.value })} className={dateInput} />
                                    </div>
                                    {view === "entries" && (
                                        <Dropdown label={eligibilityLabel} options={eligibilityOptions} value={eligibility} onChange={setEligibility} ariaLabel="Eligibility" icon={<FilterIcon className="h-[14px] w-[14px] text-[#7b8090]" />} />
                                    )}
                                    <Segmented options={viewOptions} value={view} onChange={(next) => { setView(next); setSelectedId(null); }} />
                                    <button type="button" aria-label="Refresh" onClick={() => setReloadKey((k) => k + 1)} className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-[7px] border border-[#ececef] bg-white text-[#7b8090] hover:bg-[#fafafb]">
                                        <ArrowPathIcon className="h-[15px] w-[15px]" />
                                    </button>
                                </>
                            )}
                        </div>
                        {isMobile && (
                            <div className="mt-2.5">
                                <Segmented options={viewOptions} value={view} onChange={(next) => { setView(next); setSelectedId(null); }} />
                            </div>
                        )}
                    </header>

                    <main ref={contentRef} className="px-4 pb-10 sm:px-7">
                        {!isMobile && data && !loading && !notStarted && (
                            <div className="mt-4 grid grid-cols-2 gap-2.5 lg:grid-cols-5">
                                <Stat
                                    label="Videos submitted"
                                    value={formatCount(submissionStats.total)}
                                    hint={`${formatCount(submissionStats.approved)} approved · ${formatCount(submissionStats.pending)} pending${Number(submissionStats.revisionRequested) > 0 ? ` · ${formatCount(submissionStats.revisionRequested)} needs revision` : ""}`}
                                />
                                <Stat label="Videos with sales" value={formatCount(summary.entries)} hint={`${formatCount(summary.eligibleEntries)} eligible`} />
                                <Stat label="Eligible GMV" value={formatMoney(summary.eligibleGmv)} hint={`${formatMoney(summary.totalGmv)} across all tagged entries`} />
                                <Stat label="Creators" value={formatCount(summary.creatorsSubmitted)} hint={`${formatCount(summary.creators)} with sales`} />
                                <Stat label="Unmatched GMV" value={formatMoney(summary.unmatchedGmv)} hint={`${formatCount(Array.isArray(summary.unmatchedTrybeIds) ? summary.unmatchedTrybeIds.length : 0)} trybe IDs with no Trybe submission`} />
                            </div>
                        )}
                        {data?.window?.clamped && !loading && (
                            <div className="mt-3 flex items-center gap-2 rounded-[8px] bg-[#eff6ff] px-3 py-2 text-[12.5px] text-[#1d4ed8]">
                                Showing {formatYmd(range.startDate)} through {formatYmd(range.endDate)}: days after yesterday have no final sales yet.
                            </div>
                        )}
                        {loading ? (
                            <div className="mt-4 flex min-h-[320px] items-center justify-center rounded-[10px] border border-[#ececef] bg-white">
                                <LoaderSpinner />
                            </div>
                        ) : error ? (
                            <div className="mt-4 flex items-start gap-2 rounded-[10px] border border-[#fecaca] bg-[#fef2f2] px-4 py-3 text-[13px] text-[#b91c1c]">
                                <ExclamationTriangleIcon className="mt-px h-4 w-4 flex-none" />
                                <span>{error}</span>
                            </div>
                        ) : (
                            <>
                                {view === "winners" && renderWinners()}
                                {view === "entries" && renderEntries()}
                                {view === "creators" && renderCreators()}
                                {view === "reconciliation" && renderReconciliation()}
                                <p className="mt-4 text-[11.5px] leading-[1.5] text-[#a2a6b3]">
                                    GMV is Shopify total sales (incl. tax and shipping, net of returns booked in range) on orders whose UTM content carries trybe=&lt;id&gt;. It is a proxy until Trybe exposes per-submission GMV.
                                    Only videos submitted within the selected dates count. Eligible means an approved video submission with sales in the range. Spend and ROAS use Meta ads whose name or URL carries the same trybe ID.
                                    Videos submitted counts every video submission created in the date range (UTC), whether or not it has sales.
                                </p>
                            </>
                        )}
                    </main>
                </div>
                {selected && <EntryDrawer entry={selected} onClose={closeDrawer} />}
            </div>
        </div>
    );
};

export default TrybeContest;
